<template>
  <div class="adresse-ban-search">
    <div class="edit-field-label">Rechercher une adresse</div>
    <div class="ban-field-wrapper">
      <v-text-field
        v-model="query"
        variant="outlined"
        single-line
        persistent-placeholder
        placeholder="N° et voie, ville…"
        hide-details
        autocomplete="off"
        :loading="loading"
        :disabled="disabled"
        prepend-inner-icon="fas fa-magnifying-glass"
        @update:model-value="onSearch"
        @focus="focused = true"
        @blur="focused = false">
      </v-text-field>
      <v-card v-if="showList" class="ban-results" elevation="4">
        <v-list density="compact">
          <template v-if="items.length">
            <v-list-item
              v-for="(item, index) in items"
              :key="index"
              :title="item.label"
              @mousedown.prevent="onSelect(item)">
            </v-list-item>
          </template>
          <v-list-item v-else :title="'Aucune adresse trouvée'" :disabled="true"></v-list-item>
        </v-list>
      </v-card>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from "vue";

  interface BanItem {
    label: string;
    adresse1: string;
    codePostal: string;
    ville: string;
  }

  interface BanFeature {
    properties?: {
      label?: string;
      name?: string;
      postcode?: string;
      city?: string;
    };
  }

  export default defineComponent({
    props: {
      disabled: {
        type: Boolean,
      },
    },
    emits: ['select'],
    data() {
      return {
        query: '' as string,
        items: [] as BanItem[],
        loading: false,
        focused: false,
        searched: false,
        debounceTimer: 0,
        requestId: 0,
      };
    },
    computed: {
      // Affiche la liste seulement quand le champ a le focus et qu'une recherche
      // a abouti (résultats ou "aucun résultat").
      showList(): boolean {
        return this.focused && this.searched && !this.loading;
      },
    },
    beforeUnmount(): void {
      if (this.debounceTimer) {
        window.clearTimeout(this.debounceTimer);
      }
    },
    methods: {
      onSearch(value: string): void {
        if (this.debounceTimer) {
          window.clearTimeout(this.debounceTimer);
        }
        const term = (value || '').trim();
        if (term.length < 3) {
          this.items = [];
          this.searched = false;
          this.loading = false;
          return;
        }
        this.debounceTimer = window.setTimeout(() => {
          this.fetchAddresses(term);
        }, 300);
      },

      fetchAddresses(term: string): void {
        const reqId = ++this.requestId;
        this.loading = true;
        const url =
          'https://api-adresse.data.gouv.fr/search/?q=' +
          encodeURIComponent(term) +
          '&limit=8&autocomplete=1';
        fetch(url)
          .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
          .then((data) => {
            // Ignore les réponses obsolètes (une requête plus récente est partie).
            if (reqId !== this.requestId) {
              return;
            }
            const features: BanFeature[] = (data && data.features) || [];
            this.items = features.map((f: BanFeature) => {
              const p = f.properties || {};
              return {
                label: p.label || '',
                adresse1: p.name || '',
                codePostal: p.postcode || '',
                ville: p.city || '',
              } as BanItem;
            });
            this.searched = true;
          })
          .catch(() => {
            if (reqId === this.requestId) {
              this.items = [];
              this.searched = true;
            }
          })
          .finally(() => {
            if (reqId === this.requestId) {
              this.loading = false;
            }
          });
      },

      onSelect(item: BanItem): void {
        this.$emit('select', {
          adresse1: item.adresse1,
          codePostal: item.codePostal,
          ville: item.ville,
        });
        // Réinitialise le champ pour qu'il redevienne neutre après remplissage.
        this.query = '';
        this.items = [];
        this.searched = false;
        this.focused = false;
      },
    },
  });
</script>

<style scoped>
  /* Reproduit l'espace réservé aux messages de validation des autres champs
     (EditField n'utilise pas hide-details), pour un écart vertical homogène. */
  .adresse-ban-search {
    margin-bottom: 22px;
  }

  .ban-field-wrapper {
    position: relative;
  }

  .ban-results {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 10;
    margin-top: 2px;
    max-height: 320px;
    overflow-y: auto;
  }
</style>
